import { createElement, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { BlockRecord } from '@/lib/blocks/types';
import type { BlockPropertyRecord, DatabaseColumnDefinition } from '@/lib/databases/types';
import { placedItemColorClass, rowOptionColor } from '@/lib/databases/row-color';
import {
  buildTimelineSpanDays,
  formatTimelineRangeLabel,
  groupTimelineByMonth,
  groupTimelineByQuarter,
  groupTimelineWeeks,
  TIMELINE_LABEL_WIDTH_PX,
  TIMELINE_SPANS,
  timelineBarPlacements,
  timelineBounds,
  timelineDayWidth,
  timelineStepLabel,
  todayIsoDate,
  shiftTimelineAnchor,
  type TimelineSpan,
  type TimelineWeekGroup,
} from '@/lib/databases/timeline-grid';

type DatabaseTimelineProps = {
  rows: BlockRecord[];
  propertiesByRowId: Record<string, BlockPropertyRecord[]>;
  timelineColumnId: string;
  colorColumn?: DatabaseColumnDefinition;
  onOpenRow?: (rowId: string) => void;
  onPeekRow?: (rowId: string) => void;
};

function stopBubble(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

function renderBands(bands: TimelineWeekGroup[], dayWidth: number, minor: boolean) {
  return createElement(
    'div',
    { className: 'cs-db-timeline-bands' },
    ...bands.map((band) =>
      createElement(
        'div',
        {
          key: band.key,
          className: minor ? 'cs-db-timeline-band cs-db-timeline-band-minor' : 'cs-db-timeline-band',
          style: { width: `${band.span * dayWidth}px` },
          title: band.label,
        },
        band.label,
      ),
    ),
  );
}

export function DatabaseTimeline({
  rows,
  propertiesByRowId,
  timelineColumnId,
  colorColumn,
  onOpenRow,
  onPeekRow,
}: DatabaseTimelineProps) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [span, setSpan] = useState<TimelineSpan>('month');
  const [availablePx, setAvailablePx] = useState(0);
  const [scrollToken, setScrollToken] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const label = el.querySelector('.cs-db-timeline-label-head');
      const labelWidth =
        label instanceof HTMLElement ? label.offsetWidth : TIMELINE_LABEL_WIDTH_PX;
      setAvailablePx(Math.max(0, el.clientWidth - labelWidth));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  function go(delta: number) {
    const next = shiftTimelineAnchor(year, month, span, delta);
    setYear(next.year);
    setMonth(next.month);
  }

  function goToday() {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth());
    setScrollToken((token) => token + 1);
  }

  const bounds = useMemo(() => timelineBounds(year, month, span), [year, month, span]);
  const days = useMemo(
    () => buildTimelineSpanDays(year, month, span),
    [year, month, span],
  );
  const dayWidth = timelineDayWidth(span, days.length, availablePx);
  const weeks = useMemo(
    () => groupTimelineWeeks(days, span === 'quarter' ? 'start' : 'range'),
    [days, span],
  );
  const months = useMemo(() => groupTimelineByMonth(days), [days]);
  const quarters = useMemo(() => groupTimelineByQuarter(days), [days]);
  const { placements, undated } = useMemo(
    () => timelineBarPlacements(rows, propertiesByRowId, timelineColumnId, days, dayWidth),
    [rows, propertiesByRowId, timelineColumnId, days, dayWidth],
  );
  const trackWidthPx = days.length * dayWidth;
  const todayIso = todayIsoDate();
  const todayIndex = days.findIndex((d) => d.iso === todayIso);
  const step = timelineStepLabel(span);

  const guideLefts = useMemo(() => {
    if (span === 'month') {
      return [];
    }
    const lefts: number[] = [];
    let seen = 0;
    for (const group of months) {
      if (seen > 0) {
        lefts.push(seen * dayWidth);
      }
      seen += group.span;
    }
    return lefts;
  }, [months, span, dayWidth]);

  const alignedKey = useRef('');

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const key =
      scrollToken > 0
        ? `today:${scrollToken}:${dayWidth}`
        : `anchor:${span}:${year}:${month}:${dayWidth}`;
    if (alignedKey.current === key) {
      return;
    }
    alignedKey.current = key;
    if (scrollToken > 0 && todayIndex >= 0) {
      const target =
        TIMELINE_LABEL_WIDTH_PX + todayIndex * dayWidth + dayWidth / 2 - el.clientWidth / 2;
      el.scrollLeft = Math.max(0, target);
      return;
    }
    if (span === 'month' || el.scrollWidth <= el.clientWidth + 1) {
      el.scrollLeft = 0;
      return;
    }
    const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;
    const monthStart = days.findIndex((day) => day.iso.startsWith(prefix));
    if (monthStart < 0) {
      return;
    }
    el.scrollLeft = Math.max(0, TIMELINE_LABEL_WIDTH_PX + monthStart * dayWidth - 16);
  }, [scrollToken, todayIndex, dayWidth, span, year, month, days]);

  function openRow(rowId: string) {
    (onPeekRow ?? onOpenRow)?.(rowId);
  }

  const topBands = span === 'year' ? quarters : span === 'quarter' ? months : weeks;
  const showQuarterWeeks = span === 'quarter' && dayWidth >= 8;
  const bottomBands = span === 'year' ? months : showQuarterWeeks ? weeks : null;

  return createElement(
    'div',
    {
      className: span === 'year' ? 'cs-db-timeline cs-db-timeline-year' : 'cs-db-timeline',
      style: { ['--cs-db-timeline-day-width' as string]: `${dayWidth}px` },
      onMouseDown: stopBubble,
      onKeyDownCapture: stopBubble,
    },
    createElement(
      'div',
      { className: 'cs-db-timeline-header' },
      createElement(
        'button',
        {
          type: 'button',
          className: 'cs-db-timeline-nav',
          'aria-label': `Previous ${step}`,
          onClick: () => go(-1),
        },
        '‹',
      ),
      createElement('span', { className: 'cs-db-timeline-title' }, bounds.label),
      createElement(
        'button',
        {
          type: 'button',
          className: 'cs-db-timeline-nav',
          'aria-label': `Next ${step}`,
          onClick: () => go(1),
        },
        '›',
      ),
      createElement(
        'div',
        { className: 'cs-db-timeline-span', role: 'group', 'aria-label': 'Visible range' },
        ...TIMELINE_SPANS.map((option) =>
          createElement(
            'button',
            {
              key: option.id,
              type: 'button',
              className: 'cs-db-timeline-span-btn',
              'aria-pressed': span === option.id,
              onClick: () => setSpan(option.id),
            },
            option.label,
          ),
        ),
      ),
      createElement(
        'button',
        { type: 'button', className: 'cs-db-timeline-today', onClick: goToday },
        'Today',
      ),
    ),
    createElement(
      'div',
      { className: 'cs-db-timeline-scroll', ref: scrollRef },
      createElement(
        'div',
        { className: 'cs-db-timeline-grid', style: { minWidth: `${trackWidthPx + TIMELINE_LABEL_WIDTH_PX}px` } },
        createElement(
          'div',
          { className: 'cs-db-timeline-scale' },
          createElement('div', { className: 'cs-db-timeline-label-head' }, 'Name'),
          createElement(
            'div',
            { className: 'cs-db-timeline-track-head', style: { width: `${trackWidthPx}px` } },
            span === 'month'
              ? createElement(
                  'div',
                  { className: 'cs-db-timeline-weeks' },
                  ...weeks.map((week) =>
                    createElement(
                      'div',
                      {
                        key: week.key,
                        className: 'cs-db-timeline-week',
                        style: { width: `${week.span * dayWidth}px` },
                      },
                      week.label,
                    ),
                  ),
                )
              : renderBands(topBands, dayWidth, false),
            span === 'month'
              ? createElement(
                  'div',
                  { className: 'cs-db-timeline-days' },
                  ...days.map((day) =>
                    createElement(
                      'div',
                      {
                        key: day.iso,
                        className: [
                          'cs-db-timeline-day',
                          day.inMonth ? '' : 'cs-db-timeline-day-out',
                          day.isToday ? 'cs-db-timeline-day-today' : '',
                        ]
                          .filter(Boolean)
                          .join(' '),
                        style: { width: `${dayWidth}px` },
                      },
                      String(day.dayOfMonth),
                    ),
                  ),
                )
              : bottomBands
                ? renderBands(bottomBands, dayWidth, true)
                : null,
          ),
        ),
        createElement(
          'div',
          { className: 'cs-db-timeline-body' },
          ...guideLefts.map((left) =>
            createElement('div', {
              key: left,
              className: 'cs-db-timeline-guide',
              style: { left: `${TIMELINE_LABEL_WIDTH_PX + left}px` },
            }),
          ),
          todayIndex >= 0
            ? createElement('div', {
                className: 'cs-db-timeline-today-line',
                style: {
                  left: `${TIMELINE_LABEL_WIDTH_PX + todayIndex * dayWidth + dayWidth / 2}px`,
                },
              })
            : null,
          placements.length === 0
            ? createElement(
                'div',
                { className: 'cs-db-timeline-empty' },
                `No items in ${bounds.label}.`,
              )
            : placements.map((placement) => {
                const title = placement.row.title?.trim() || 'Untitled';
                const rangeLabel = formatTimelineRangeLabel(
                  placement.startIso,
                  placement.endIso,
                );
                return createElement(
                  'div',
                  { key: placement.row.id, className: 'cs-db-timeline-row' },
                  createElement(
                    'button',
                    {
                      type: 'button',
                      className: 'cs-db-timeline-label',
                      title,
                      onClick: () => openRow(placement.row.id),
                      onDoubleClick: onOpenRow
                        ? () => onOpenRow(placement.row.id)
                        : undefined,
                      onMouseDown: stopBubble,
                    },
                    title,
                  ),
                  createElement(
                    'div',
                    {
                      className: 'cs-db-timeline-track',
                      style: { width: `${trackWidthPx}px` },
                    },
                    createElement(
                      'button',
                      {
                        type: 'button',
                        className: [
                          'cs-db-timeline-bar',
                          placement.extendsBefore ? 'cs-db-timeline-bar-before' : '',
                          placement.extendsAfter ? 'cs-db-timeline-bar-after' : '',
                          placedItemColorClass(
                            rowOptionColor(propertiesByRowId[placement.row.id], colorColumn),
                          ),
                        ]
                          .filter(Boolean)
                          .join(' '),
                        style: {
                          left: `${placement.leftPx}px`,
                          width: `${Math.max(placement.widthPx, 6)}px`,
                        },
                        title: `${title} · ${rangeLabel}`,
                        onClick: () => openRow(placement.row.id),
                        onDoubleClick: onOpenRow
                          ? () => onOpenRow(placement.row.id)
                          : undefined,
                        onMouseDown: stopBubble,
                      },
                      placement.widthPx >= 48
                        ? createElement('span', { className: 'cs-db-timeline-bar-label' }, title)
                        : null,
                    ),
                  ),
                );
              }),
        ),
      ),
    ),
    undated.length > 0
      ? createElement(
          'div',
          { className: 'cs-db-timeline-undated' },
          createElement(
            'div',
            { className: 'cs-db-timeline-undated-label' },
            `No date (${undated.length})`,
          ),
          createElement(
            'div',
            { className: 'cs-db-timeline-undated-list' },
            ...undated.map((row) =>
              createElement(
                'button',
                {
                  key: row.id,
                  type: 'button',
                  className: [
                    'cs-db-timeline-undated-item',
                    placedItemColorClass(rowOptionColor(propertiesByRowId[row.id], colorColumn)),
                  ]
                    .filter(Boolean)
                    .join(' '),
                  onClick: () => openRow(row.id),
                  onDoubleClick: onOpenRow ? () => onOpenRow(row.id) : undefined,
                },
                row.title?.trim() || 'Untitled',
              ),
            ),
          ),
        )
      : null,
  );
}
